import useUser from '@/mp/hooks/useUser';
import routes from '@/routes/constants';
import { formatCurrency } from '@/text';
import { ActivityMetadata } from '@larki/shared/types/activity.type';
import { JobT } from '@larki/shared/types/job.type';
import { format } from 'date-fns';
import { useCallback } from 'react';
import { Link } from 'react-router-dom';

export default function InvoiceActivity({
  job,
  metadata,
}: {
  job: JobT;
  metadata: ActivityMetadata;
}) {
  const invoiceMetadata = metadata.variant === 'invoice' ? metadata : null;
  const currentUser = useUser();
  const getRedirectUrl = useCallback(
    (key: 'order' | 'membership' | undefined) => {
      if (!invoiceMetadata) return null;
      const step =
        currentUser.isEssentialUser || currentUser.isGovernment ? 2 : 3;
      // payQuoteId → the payment page pays this issued invoice's quote at its
      // locked price, so a collaborator paying doesn't re-quote live.
      const params = new URLSearchParams({
        mp: String(job.map2d_id),
        step: String(step),
        payQuoteId: String(invoiceMetadata.quote_id),
      });
      if (
        invoiceMetadata.membership_product &&
        invoiceMetadata.status !== 'PAID'
      ) {
        params.set('membership', invoiceMetadata.membership_product);
      }
      const paymentUrl = `${routes.mp.payment(job.id)}?${params.toString()}`;

      if (
        invoiceMetadata?.status === 'PAID' &&
        (invoiceMetadata.order_invoice_url ||
          invoiceMetadata.membership_invoice_url)
      ) {
        if (key === 'order') {
          return invoiceMetadata.order_invoice_url;
        }
        if (key === 'membership') {
          return invoiceMetadata.membership_invoice_url;
        }
      }
      return paymentUrl;
    },
    [
      job.id,
      job.map2d_id,
      metadata.variant,
      currentUser.isEssentialUser,
      currentUser.isGovernment,
      invoiceMetadata
    ]
  );

  if (metadata.variant !== 'invoice') return null;

  return (
    <>
      <p className='font-weight--600 mb-3 font-18'>INVOICE</p>
      <div className='d-flex flex-row mb-3'>
        <div className='d-flex flex-column flex-fill'>
          <div className='d-flex flex-row'>
            <p className='mb-0'>Invoice No.</p>
            <p className='ml-2 mb-0'>{metadata.order_invoice_number ?? '-'}</p>
          </div>
          <div className='d-flex flex-column mt-3'>
            <p className='mb-0'>Issued:</p>
            <p className='mb-0 font-weight--600'>
              {format(new Date(metadata.issued_date), 'dd MMM yyyy')}
            </p>
          </div>
          <div className='d-flex flex-column mt-3'>
            <p className='mb-0'>Due:</p>
            <p className='mb-0 font-weight--600'>
              {metadata.due_date
                ? format(new Date(metadata.due_date), 'dd MMM yyyy')
                : 'N/A'}
            </p>
          </div>
        </div>
        <div className='d-flex flex-column flex-fill'>
          <div className='d-flex flex-column'>
            <p className='mb-0'>Attention:</p>
            <p className='mb-0'>{metadata.invoiced_to}</p>
          </div>
          <div className='d-flex flex-column mt-3'>
            <p className='mb-0'>Billed to:</p>
            <p className='mb-0'>{metadata.billed_to.company ?? ''}</p>
            <p className='mb-0'>{metadata.billed_to.address ?? ''}</p>
            <p className='mb-0'>{metadata.billed_to.phone ?? ''}</p>
          </div>
        </div>
        <div className='d-flex flex-column flex-fill'>
          <div className='d-flex flex-column'>
            <p className='mb-0'>Status:</p>
            <p className='mb-0 font-weight--600'>{metadata.status}</p>
          </div>
          <div className='d-flex flex-column mt-3'>
            <p className='mb-0'>Amount (inc GST):</p>
            <p className='mb-0 font-weight--600'>
              {formatCurrency(metadata.amount, 'aud', 100, {}, true)}
            </p>
          </div>
        </div>
      </div>
      {metadata.status === 'PAID' ? (
        <div className='d-flex flex-row flex-gap-12'>
          <Link
            className='font-weight-bold'
            style={{ textDecoration: 'underline' }}
            key='viewOrderInvoice'
            to={{ pathname: getRedirectUrl('order') ?? '' }}
            target='_blank'
          >
            View {getRedirectUrl('membership') && 'Order '}Invoice
          </Link>
          {getRedirectUrl('membership') && (
            <Link
              className='font-weight-bold'
              style={{ textDecoration: 'underline' }}
              key='viewMembershipInvoice'
              to={{ pathname: getRedirectUrl('membership') ?? '' }}
              target='_blank'
            >
              View Membership Invoice
            </Link>
          )}
        </div>
      ) : (
        <Link
          className='font-weight-bold'
          style={{ textDecoration: 'underline' }}
          key='payeInvoice'
          to={{ pathname: getRedirectUrl(undefined) ?? '' }}
          target='_blank'
        >
          Pay Invoice
        </Link>
      )}
    </>
  );
}
